import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function DemoBillsList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [bills, setBills] = useState([]);
    const [selectedBills, setSelectedBills] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredBills, setFilteredBills] = useState([]);

    const refreshBills = async () => {
        const res = await api.get('/proformabills');
        const sortedBills = res.data.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate));
        setBills(sortedBills);
        setFilteredBills(sortedBills);
    };

    useEffect(() => {
        withLoading(refreshBills).catch((err) => console.log(err));
    }, [withLoading]);

    useEffect(() => {
        const results = bills.filter(bill =>
            bill.partyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bill.workTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bill.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredBills(results);
    }, [searchTerm, bills]);

    const handleCheckboxChange = (billId) => {
        setSelectedBills(prev =>
            prev.includes(billId) ? prev.filter(id => id !== billId) : [...prev, billId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedBills(e.target.checked ? filteredBills.map(bill => bill.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected bills?')) return;
        Promise.all(selectedBills.map(billId => api.delete(`/proformabills/${billId}`)))
            .then(() => {
                setBills(prev => prev.filter(bill => !selectedBills.includes(bill.id)));
                setSelectedBills([]);
            })
            .catch(err => console.log(err));
    };

    const handleDuplicate = async (bill) => {
        try {
            const response = await api.get(`/proformabills/${bill.id}`);
            const completeBill = response.data;
            const duplicateData = {
                ...completeBill,
                partyName: `${completeBill.partyName} (Duplicate)`,
                invoiceDate: new Date().toISOString().split('T')[0],
                invoiceNo: '',
                id: undefined
            };
            await api.post('/proformabills', duplicateData);
            await refreshBills();
        } catch (error) {
            console.error('Error duplicating bill:', error);
            alert('Failed to duplicate bill');
        }
    };

    const formatCurrency = (value) =>
        Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const getTotalWithGst = (bill) => {
        if (!bill.gstRate) return Number(bill.totalAmount || 0);
        const totalGST = parseFloat((bill.gstRate * 2) / 100);
        const totalAmount = parseFloat(bill.totalAmount);
        const gstAmount = parseFloat((totalAmount * totalGST).toFixed(2));
        return parseFloat((totalAmount + gstAmount).toFixed(2));
    };

    const allSelected = filteredBills.length > 0 && selectedBills.length === filteredBills.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Proforma Invoices</h1>
                    <p className="list-page-subtitle">
                        {filteredBills.length} proforma invoice{filteredBills.length === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search invoice, party, or work title"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                    </div>
                    <div className="list-button-group">
                        <button type="button" onClick={() => navigate('/createDemoBill')} className="list-add-button">
                            Add New Proforma
                        </button>
                        {selectedBills.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedBills.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Invoice No</th>
                                <th>Date</th>
                                <th>Party Name</th>
                                <th>Work Title</th>
                                <th className="col-num">Amount</th>
                                <th className="col-num">GST %</th>
                                <th className="col-num">Total (Inc. GST)</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBills.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="9">
                                        <div className="list-empty-state">
                                            <h3>No proforma invoices found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Create your first proforma invoice to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredBills.map(bill => (
                                <tr
                                    key={bill.id}
                                    className={`list-row${selectedBills.includes(bill.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox' && e.target.type !== 'button') {
                                            navigate(`/createDemoBill/${bill.id}`);
                                        }
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedBills.includes(bill.id)}
                                            onChange={() => handleCheckboxChange(bill.id)}
                                        />
                                    </td>
                                    <td><span className="list-primary-text">{bill.invoiceNo}</span></td>
                                    <td>{formatDateDDMMYYYY(bill.invoiceDate)}</td>
                                    <td>{bill.partyName}</td>
                                    <td><span className="list-muted-text">{bill.workTitle}</span></td>
                                    <td className="col-num">{formatCurrency(bill.totalAmount)}</td>
                                    <td className="col-num">{(Number(bill.gstRate || 0)).toFixed(2)}</td>
                                    <td className="col-num list-primary-text">{formatCurrency(getTotalWithGst(bill))}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <button type="button" className="list-secondary-button" onClick={() => handleDuplicate(bill)}>
                                            Duplicate
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default DemoBillsList;
